import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'wouter';
import {
  login as apiLogin,
  logout as apiLogout,
  verifyToken,
  isAuthenticated as checkAuth,
  getStoredUsername,
} from '../lib/admin-api';

interface UseAdminAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  username: string | null;
  error: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

export function useAdminAuth(): UseAdminAuthReturn {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [username, setUsername] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [, setLocation] = useLocation();

  // Check authentication on mount
  useEffect(() => {
    const checkAuthentication = async () => {
      setIsLoading(true);

      if (!checkAuth()) {
        setIsAuthenticated(false);
        setUsername(null);
        setIsLoading(false);
        return;
      }

      try {
        const result = await verifyToken();
        setIsAuthenticated(result.valid);
        setUsername(result.username);
      } catch {
        // Token invalid, clear it
        apiLogout();
        setIsAuthenticated(false);
        setUsername(null);
      }

      setIsLoading(false);
    };

    checkAuthentication();
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await apiLogin(username, password);
      setIsAuthenticated(true);
      setUsername(result.username);
      setIsLoading(false);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      setIsAuthenticated(false);
      setUsername(null);
      setIsLoading(false);
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    apiLogout();
    setIsAuthenticated(false);
    setUsername(null);
    setLocation('/admin/login');
  }, [setLocation]);

  return {
    isAuthenticated,
    isLoading,
    username: username || getStoredUsername(),
    error,
    login,
    logout,
  };
}

// Hook to require authentication
export function useRequireAdminAuth(): {
  isLoading: boolean;
  isAuthenticated: boolean;
} {
  const { isAuthenticated, isLoading } = useAdminAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setLocation('/admin/login');
    }
  }, [isLoading, isAuthenticated, setLocation]);

  return { isLoading, isAuthenticated };
}
